import type { Profile } from "@shared/types/supabase.types";
import { supabase } from "@/lib/supabase";

/**
 * Fetch a user's profile by their user ID
 */
export async function getProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (error) {
    throw new Error(`Failed to fetch profile for user=${userId}: ${error.message}`);
  }

  return data;
}

/**
 * Upsert a user profile
 */
export async function upsertProfile(profile: Profile): Promise<Profile> {
  const { data, error } = await supabase
    .from("profiles")
    .upsert({
      ...profile,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to upsert profile: ${error.message}`);
  }

  return data;
}

/**
 * Update a user's avatar URL
 */
export async function updateAvatarUrl({
  userId,
  avatarUrl,
}: {
  userId: string;
  avatarUrl: string | null;
}): Promise<Profile> {
  console.log(`Updating avatar URL for user ${userId} to: ${avatarUrl}`);

  const { data, error } = await supabase
    .from("profiles")
    .update({ avatar_url: avatarUrl })
    .eq("user_id", userId)
    .select()
    .single();

  console.log("Avatar URL update result:", { data, error });

  if (error) {
    throw new Error(`Failed to update avatar URL: ${error.message}`);
  }

  return data;
}

/**
 * Create a realtime subscription for a user's profile
 */
export function createProfileSubscription(userId: string, callback: (payload: any) => void) {
  const channel = supabase
    .channel(`profile-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "profiles",
        filter: `user_id=eq.${userId}`,
      },
      callback,
    )
    .subscribe();

  return channel;
}
